import React, { useState } from 'react';

import { Box, Button, CircularProgress, Stack } from '@mui/material';
import dompurify from 'dompurify';

import dayjs from '@/components/NewUI/utils/dayjs';

import DebounceInput from './DebounceInput';
import { classes } from './styles';
import { theme } from '../../../ThemeContext/ThemeObject';
import AvailabilityPreview from '../../ApprovalForms/Questions/Modals/AvailabilityPreview';
import FormCheckboxGroup from '../CustomUIElements/FormCheckboxGroup';
import FormRadioButtonGroup from '../CustomUIElements/FormRadioButtonGroup';
import FormRadioButtonWithTextInputGroup from '../CustomUIElements/FormRadioButtonWithTextInputGroup';
import FormRankField from '../CustomUIElements/FormRankField';
import FormSelectField from '../CustomUIElements/FormSelectField';
import { FormTextField, MultilineFormTextField } from '../CustomUIElements/FormTextField';
import { sharedClasses } from '../CustomUIElements/sharedClasses';
import { UncontrolledSingleDatePicker } from '../CustomUIElements/SingleDatePicker';

import type { SxProps } from '@mui/material';
import type { FC, PropsWithChildren } from 'react';

export interface IApplicantQuestion {
  id: number;
  required: boolean;
  enabled: boolean;
  position: number;
  title: string;
  type: string;
  field_choices: IFieldChoice[];
  availability_config: IAvailabilityConfig;
  original_title: string;
  for_email_template: boolean;
  rateable: boolean;
}

export interface IAvailabilityConfig {
  allow_break: boolean;
  created_at: string;
  field_id: number;
  heading_1: string;
  heading_2: string;
  id: number;
  updated_at: string;
  week_duration: string;
  week_start_day: number;
}

export interface IFieldChoice {
  id: number;
  name: string;
  position: number;
}

interface QuestionHTMLProps {
  sx?: SxProps;
  html: string | TrustedHTML;
}

// sx is merged with MUI's array form rather than spread: SxProps can be a
// callback, and spreading a function silently produces an empty object.
const QuestionHTML: FC<PropsWithChildren<QuestionHTMLProps>> = ({ html, sx, children }) => (
  <Box sx={[classes.inputLabel, ...(Array.isArray(sx) ? sx : [sx])] as SxProps}>
    <span
      // Every caller passes dompurify.sanitize output; this component exists so that
      // sanitising happens in one place rather than at each question type.
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: html
      }}
    />
    {children}
  </Box>
);

const QuestionFieldGenerator = ({
  question,
  onChangeCallback,
  questionRef,
  questionError,
  handleLookUpButton,
  answer,
  fetchingCSV,
  disabled = false,
  inputText,
  setApprovalFormQuestions,
  approvalPage,
  fullWidth,
  clearable = false
}: {
  question: IApplicantQuestion;
  onChangeCallback: (id: number, value: string | number | Record<string, boolean | string>) => void;
  questionRef?: (el: HTMLDivElement) => void;
  questionError?: string;
  handleLookUpButton?: (questionId: number, positionId: number) => void;
  answer?: string | Record<string, string>;
  fetchingCSV?: boolean;
  disabled?: boolean;
  inputText?: string;
  setApprovalFormQuestions?: (value: Record<string, { data: string; inputText: string }>) => void;
  approvalPage?: boolean;
  fullWidth?: boolean;
  clearable?: boolean;
}): React.ReactElement | null => {
  const csvOptions = ['Please choose...', 'Existing Position', 'New Position'];
  const [selectedCSVOption, setSelectedCSVOption] = useState<string>(csvOptions[0]);
  const [positionId, setPositionId] = useState<string>('');
  const questionHTMLTemplate =
    dompurify.sanitize(question.title) +
    (question.required ? `<span style="color: ${theme.palette.error.main}">*</span>` : '');

  // Seeded from the parent's answer, then owned locally so the user can edit
  // it. Adjusted during render rather than in an effect: an effect would
  // render once with the stale value and again with the seeded one on every
  // answer change, which is the cascading render react-hooks warns about.
  // Guarded on the answer's identity so a user's own edit is not immediately
  // overwritten by this same seeding.
  const [seededFrom, setSeededFrom] = useState<typeof answer>();
  if (question.type === 'CsvDataLookupField' && answer.length > 0 && answer !== seededFrom) {
    setSeededFrom(answer);
    setSelectedCSVOption(answer[0]);
    setPositionId(answer[1]);
  }

  switch (question.type) {
    case 'RadioButtonField':
      return (
        <Box>
          <QuestionHTML
            html={
              dompurify.sanitize(question.title.replace(/\n/g, '<br>')) +
              (question.required ? `<span style="color: ${theme.palette.error.main}">*</span>` : '')
            }
            sx={{
              ...(question.for_email_template ? { color: '#333333 !important' } : {})
            }}
          />
          {question.for_email_template && approvalPage ? (
            <FormRadioButtonWithTextInputGroup
              defaultValue={answer}
              draggable={disabled}
              error={questionError}
              innerRef={questionRef}
              inputText={inputText}
              onChange={(value: string | number) => onChangeCallback(question.id, value)}
              question={question}
              setApprovalFormQuestions={setApprovalFormQuestions}
              options={question.field_choices.map((choice) => ({
                label: choice.name,
                value: choice.id
              }))}
            />
          ) : (
            <FormRadioButtonGroup
              clearable={clearable}
              defaultValue={answer}
              draggable={disabled}
              error={questionError}
              innerRef={questionRef}
              onChange={(value: string | number) => onChangeCallback(question.id, value)}
              options={question.field_choices.map((choice) => ({
                label: choice.name,
                value: choice.id
              }))}
            />
          )}
        </Box>
      );
    case 'CheckBoxField':
      return (
        <Box>
          <QuestionHTML html={questionHTMLTemplate} />
          <FormCheckboxGroup
            defaultValues={answer}
            disabled={disabled}
            error={questionError}
            innerRef={questionRef}
            onChange={(value) => onChangeCallback(question.id, value)}
            options={question.field_choices}
          />
        </Box>
      );
    case 'TextField':
      return (
        <Box>
          <QuestionHTML html={questionHTMLTemplate} />
          <DebounceInput
            defaultValue={answer}
            onDebouncedChange={(value: string) => onChangeCallback(question.id, value)}
          >
            {({ value, onChange }) => (
              <FormTextField
                draggable={disabled}
                error={questionError}
                innerRef={questionRef}
                onChange={onChange}
                required={question.required}
                styles={{ width: '290px' }}
                value={value}
              />
            )}
          </DebounceInput>
        </Box>
      );
    case 'TextAreaField':
      return (
        <Box>
          <QuestionHTML html={questionHTMLTemplate} />
          <DebounceInput
            defaultValue={answer}
            onDebouncedChange={(value: string) => onChangeCallback(question.id, value)}
          >
            {({ value, onChange }) => (
              <MultilineFormTextField
                draggable={disabled}
                error={questionError}
                fullWidth={fullWidth}
                innerRef={questionRef}
                onChange={onChange}
                required={question.required}
                value={value}
                styles={{
                  width: fullWidth ? '100%' : '600px',
                  minWidth: fullWidth ? '100%' : '600px'
                }}
              />
            )}
          </DebounceInput>
        </Box>
      );
    case 'SelectField':
      return (
        <Box>
          <QuestionHTML html={questionHTMLTemplate} />
          <FormSelectField
            clearable={clearable}
            defaultValue={answer}
            error={questionError}
            innerRef={questionRef}
            isDisabled={disabled}
            onChange={(value: string | number) => onChangeCallback(question.id, value)}
            required={question.required}
            styles={{ width: '290px' }}
            options={question.field_choices.map((choice) => ({
              label: choice.name,
              value: choice.id
            }))}
          />
        </Box>
      );
    case 'RankField':
      return (
        <Box>
          <QuestionHTML html={questionHTMLTemplate} />
          <FormRankField
            choices={question.field_choices}
            defaultValue={answer}
            disabled={disabled}
            error={questionError}
            innerRef={questionRef}
            onChange={(value) => onChangeCallback(question.id, value)}
            required={question.required}
          />
        </Box>
      );
    case 'DateField': {
      const dateFormat = 'DD/MM/YYYY';
      const parseDate = (dateString: string): Date | null => {
        if (!dateString) return null;
        const formats = ['DD/MM/YYYY', 'YYYY-MM-DD'];
        const validDate = formats
          .map((format) => dayjs(dateString, format, true))
          .find((parsed) => parsed.isValid());
        return validDate ? validDate.toDate() : null;
      };
      return (
        <Box>
          <QuestionHTML html={questionHTMLTemplate} />
          <Box ref={questionRef} sx={classes.formDateWrapper}>
            <UncontrolledSingleDatePicker
              defaultValue={answer ? parseDate(answer as string) : null}
              disabled={disabled}
              error={!!questionError}
              format={dateFormat}
              innerRef={questionRef}
              inputStyles={classes.formDatePickerStyles}
              handleDateChange={(date) => {
                let formattedDate = '';
                if (date)
                  formattedDate = date.isValid() ? date.format('YYYY-MM-DD') : 'Invalid date';
                onChangeCallback(question.id, formattedDate);
              }}
            />
            {questionError ? <Box sx={sharedClasses.errorBox}>{questionError}</Box> : null}
          </Box>
        </Box>
      );
    }
    case 'CsvDataLookupField': {
      return (
        <Box>
          <QuestionHTML html={questionHTMLTemplate} />
          <FormSelectField
            defaultValue={selectedCSVOption}
            isDisabled={disabled}
            onChange={(value: string) => setSelectedCSVOption(value)}
            options={csvOptions}
            styles={{ width: '290px' }}
            value={selectedCSVOption}
          />
          {selectedCSVOption !== csvOptions[0] && (
            <Stack sx={{ marginTop: 2 }}>
              {selectedCSVOption === 'Existing Position'
                ? 'Please enter the Existing Position Number'
                : "Please enter the Reports to Manager's Position Number"}
              <Stack sx={{ flexDirection: 'row', alignItems: 'center', marginTop: 1 }}>
                <DebounceInput
                  defaultValue={positionId}
                  onDebouncedChange={(value: string) => {
                    setPositionId(value);
                    onChangeCallback(question.id, [selectedCSVOption, value]);
                  }}
                >
                  {({ value, onChange }) => (
                    <FormTextField
                      draggable={disabled}
                      error={questionError}
                      innerRef={questionRef}
                      onChange={onChange}
                      required={question.required}
                      styles={{ width: '290px' }}
                      value={value}
                    />
                  )}
                </DebounceInput>
                <Button
                  onClick={() => handleLookUpButton(question.id, positionId)}
                  sx={classes.lookUpButton}
                >
                  {fetchingCSV ? <CircularProgress color="inherit" size={20} /> : 'Look Up'}
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      );
    }
    case 'AvailabilityField':
      return (
        <Box>
          <QuestionHTML html={questionHTMLTemplate} />
          <Box ref={questionRef} sx={classes.formDateWrapper}>
            <AvailabilityPreview
              answer={answer}
              availabilityConfig={question.availability_config}
              disabled={disabled}
              error={questionError}
              onChange={(value) => onChangeCallback(question.id, value)}
            />
          </Box>
        </Box>
      );
    case 'JobReferenceLookupField':
      return null;
    // As in QuestionFieldViewer: the real control is the HAML renderer on the
    // public form, so the preview describes the field rather than faking it.
    case 'RefereeListField':
      return (
        <Box>
          <QuestionHTML html={questionHTMLTemplate} />
          <Box ref={questionRef} sx={classes.questionHint}>
            Candidates add one row per referee: first name, last name and email are required; phone
            and relationship are optional.
          </Box>
        </Box>
      );
    default:
      return (
        <Box>
          <Box ref={questionRef}>{question.title}</Box>
          Invalid question type
        </Box>
      );
  }
};

export default QuestionFieldGenerator;
